import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Stack, Chip, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer, Divider, Tooltip, IconButton, LinearProgress, Avatar,
} from '@mui/material';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import WifiIcon from '@mui/icons-material/Wifi';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import CircleIcon from '@mui/icons-material/Circle';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../services/api';
import { getSocket } from '../socket';
import StatTile from '../components/dashboard/StatTile';
import { TestsOverTime, TransportMix } from '../components/dashboard/charts';
import FleetMap from '../components/dashboard/FleetMap';

dayjs.extend(relativeTime);

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const rel = (t) => (t ? dayjs(t).fromNow() : 'never');

const SectionCard = ({ title, action, children, sx }) => (
  <Paper sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/device/dashboard');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    // live online-status updates
    const socket = getSocket();
    const onStatus = (d) => {
      if (!d || !d.deviceId) return;
      setData((prev) => {
        if (!prev) return prev;
        const devices = prev.fleet.devices.map((x) =>
          x.deviceId === d.deviceId ? { ...x, online: !!d.online, link: d.link || x.link } : x);
        const online = devices.filter((x) => x.online).length;
        return { ...prev, fleet: { ...prev.fleet, devices, online, offline: devices.length - online } };
      });
    };
    socket.on('device-status', onStatus);
    return () => { clearInterval(iv); socket.off('device-status', onStatus); };
  }, [load]);

  const fleet = data?.fleet;
  const m = data?.metrics;
  const onlinePct = fleet && fleet.total ? Math.round((fleet.online / fleet.total) * 100) : 0;

  const tiles = useMemo(() => ([
    { label: 'Total Devices', value: fmtNum(fleet?.total), icon: <DevicesOtherOutlinedIcon fontSize="small" />, sub: `${fleet?.online || 0} online · ${fleet?.offline || 0} offline` },
    { label: 'Online Now', value: fmtNum(fleet?.online), icon: <CheckCircleOutlineIcon fontSize="small" />, sub: `${onlinePct}% of fleet` },
    { label: 'Tests · 7 days', value: fmtNum(m?.tests7d), trend: m?.trend7d, icon: <ScienceOutlinedIcon fontSize="small" />, sub: `${fmtNum(m?.testsToday)} today` },
    { label: 'Total Tests', value: fmtNum(m?.totalTests), icon: <TimelineOutlinedIcon fontSize="small" />, sub: `${fmtNum(m?.tests30d)} in 30 days` },
    { label: 'Operators', value: fmtNum(m?.uniqueUsers), icon: <PeopleAltOutlinedIcon fontSize="small" />, sub: 'unique users' },
    { label: 'Avg Elements', value: m?.avgElements ?? '—', icon: <BoltOutlinedIcon fontSize="small" />, sub: `${fmtNum(m?.avgQuantity)} avg qty` },
  ]), [fleet, m, onlinePct]);

  if (loading && !data) return <LinearProgress />;
  if (error && !data) return <Paper sx={{ p: 3 }}><Typography color="error">{error}</Typography></Paper>;

  const devices = fleet?.devices || [];
  const recent = data?.recent || [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Fleet overview · updated {data ? rel(data.generatedAt) : '—'}
        </Typography>
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>

      {/* KPI row */}
      <Grid container spacing={2}>
        {tiles.map((t) => (
          <Grid item xs={6} sm={4} md={2} key={t.label}><StatTile {...t} /></Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={8}>
          <SectionCard title="Tests over time" action={<Typography variant="caption" color="text.secondary">last 14 days</Typography>}>
            <Box sx={{ height: 260 }}><TestsOverTime series={data?.series || []} /></Box>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Transport mix">
            <Box sx={{ height: 260 }}><TransportMix transport={data?.transport || []} /></Box>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Fleet table + activity */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={7}>
          <SectionCard title="Devices" action={
            <Stack direction="row" spacing={1}>
              <Chip size="small" color="success" variant="outlined" icon={<CircleIcon sx={{ fontSize: 9 }} />} label={`${fleet?.online || 0} online`} />
              <Chip size="small" variant="outlined" label={`${fleet?.offline || 0} offline`} />
            </Stack>
          }>
            <TableContainer sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Device</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell>Firmware</TableCell>
                    <TableCell align="right">Tests</TableCell>
                    <TableCell align="right">Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No devices registered</TableCell></TableRow>
                  )}
                  {devices.map((d) => (
                    <TableRow key={d.deviceId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{d.deviceId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" color={d.online ? 'success' : 'default'}
                          icon={<CircleIcon sx={{ fontSize: 9 }} />} label={d.online ? 'Online' : 'Offline'} />
                      </TableCell>
                      <TableCell>
                        {d.online ? (
                          <Chip size="small" variant="outlined"
                            icon={d.link === '4g' ? <SignalCellularAltIcon sx={{ fontSize: 14 }} /> : <WifiIcon sx={{ fontSize: 14 }} />}
                            label={d.link === '4g' ? '4G' : 'WiFi'} />
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="body2">{d.firmware || '—'}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{fmtNum(d.tests)}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="caption" color="text.secondary">{rel(d.lastSeen)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Recent activity">
            <Stack divider={<Divider flexItem />} spacing={0} sx={{ maxHeight: 340, overflow: 'auto' }}>
              {recent.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No recent activity</Typography>}
              {recent.map((r, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                  <Avatar sx={{ width: 30, height: 30, bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main' }}>
                    <BoltOutlinedIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {(r.action || 'command').replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {r.device_id || 'unknown'} · {r.transport_mode || 'auto'}{r.element_count ? ` · ${r.element_count} elements` : ''}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.disabled" noWrap>{rel(r.timestamp)}</Typography>
                </Stack>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Map */}
      <Box sx={{ mt: 2 }}>
        <SectionCard title="Device locations">
          <FleetMap devices={devices} height={360} />
        </SectionCard>
      </Box>
    </Box>
  );
};

export default Dashboard;
